import range from 'lodash-es/range'
import React from 'react'

import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ButtonClean,
  Checkbox,
  Fieldset,
  HeadContent,
  Loading,
  RadioButton,
  SaveShortcutListener,
  Select,
  Subsection,
  Tooltip,
} from '@fourthwall/components'
import { isProduction } from '@fourthwall/utils/dist/dev'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'

import { useFreeTierQuery } from '../../../Perks/hooks/useFreeTierQuery'
import { useAccountSettingsQuery } from '../../hooks/useAccountSettingsQuery'
import { useUpdateGeneralSettingsMutation } from '../../hooks/useUpdateGeneralSettingsMutation'
import { validationSchema } from './consts'
import type { FormValues } from './types'
import { FormFields } from './types'
import { getUpdateAccountSettingsTiersPayload } from './utils'

export const General = () => {
  const { accountSettingsQuery } = useAccountSettingsQuery()
  const { freeTierQuery } = useFreeTierQuery()
  const { updateGeneralSettingsMutation } = useUpdateGeneralSettingsMutation()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const formik = useFormik<FormValues>({
    initialValues: {
      annualEnabled: !!accountSettingsQuery.data?.tiers?.annual?.discount?.enabled,
      freeAccounts: accountSettingsQuery.data?.freeAccounts.displayEnabled ? 'show' : 'hide',
      contentVisibility:
        accountSettingsQuery.data?.tiers?.contentVisibility || 'ShowLockedOnlyHigherLevel',
      anualDiscount: accountSettingsQuery.data?.tiers?.annual?.discount?.percentOff
        ? accountSettingsQuery.data?.tiers?.annual?.discount?.percentOff?.toString()
        : '10',
      membersCountEnabled: !!accountSettingsQuery.data?.tiers?.membersCountEnabled,
      adultContent: !!accountSettingsQuery.data?.adultContent.present,
      audioDownload: !!accountSettingsQuery.data?.mediaDownload.audio.enabled,
      membershipsVisible: !!accountSettingsQuery.data?.membershipsVisibility.public,
      gifEnabled: !!accountSettingsQuery.data?.gifting.enabled,
    },
    validationSchema,
    onSubmit: (values) => {
      updateGeneralSettingsMutation.mutate([getUpdateAccountSettingsTiersPayload(values)], {
        onSuccess: () => {
          accountSettingsQuery.refetch()
        },
      })
    },
    enableReinitialize: true,
  })

  const selectOptions = range(1, 26).map((number) => ({
    value: number.toString(),
    label: number.toString(),
  }))

  const renderShowFreeAccounts = () => {
    if (freeTierQuery.data && freeTierQuery.data.status === 'Published') {
      return (
        <Fieldset.Row>
          <Fieldset.Item>
            <Tooltip
              reference={
                <RadioButton
                  label="Show free account signup link"
                  suggestion="Users will see tiers selection and a link to create a free account"
                  disabled
                  value="1"
                />
              }
              placement="top"
            >
              If you want to enable this option you have to remove your free tier inside Perks &
              tiers tab.
            </Tooltip>
          </Fieldset.Item>
        </Fieldset.Row>
      )
    }

    return (
      <Fieldset.Row>
        <Fieldset.Item>
          <RadioButton
            value={formik.values.freeAccounts}
            checkedValue="show"
            label="Show free account signup link"
            suggestion="Users will see tiers selection and a link to create a free account"
            name={FormFields.freeAccounts}
            onChange={formik.handleChange}
          />
        </Fieldset.Item>
      </Fieldset.Row>
    )
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <HeadContent title="General" />
      <Loading
        isLoading={
          accountSettingsQuery.isFetching ||
          updateGeneralSettingsMutation.isLoading ||
          freeTierQuery.isLoading
        }
      >
        <Subsection title="Annual subscription">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                Supporters will be able to pay up front for a whole year of membership. The
                subscription renews automatically each year and members can cancel it at any time. A
                good practice is to offer a lower price for the annual option.
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <Checkbox
                  {...formik.getFieldProps('annualEnabled')}
                  label="Offer annual subscription"
                />
              </Fieldset.Item>
            </Fieldset.Row>
            {(formik.values.anualDiscount || formik.values.annualEnabled) && (
              <Fieldset.Row inner>
                <Select
                  {...formik.getFieldProps('anualDiscount')}
                  options={selectOptions}
                  label="Discount value"
                  prepend="%"
                  disabled={!formik.values.annualEnabled}
                />
              </Fieldset.Row>
            )}
          </Fieldset>
        </Subsection>

        <Subsection title="Free account visibility">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                Show a link to creating a free account in places where memberships tiers are
                displayed.{' '}
                <ButtonClean
                  href="https://help.fourthwall.com/hc/en-us/articles/25005490238235"
                  label="Learn more"
                  target="_blank"
                />
              </Fieldset.Item>
            </Fieldset.Row>
            {renderShowFreeAccounts()}
            <Fieldset.Row>
              <RadioButton
                value={formik.values.freeAccounts}
                checkedValue="hide"
                label="Hide free account signup link"
                suggestion="Users will only see tiers selection"
                name={FormFields.freeAccounts}
                onChange={formik.handleChange}
              />
            </Fieldset.Row>
          </Fieldset>
        </Subsection>

        <Subsection title="Post previews for lower tier members">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                Encourage lower-tier members to upgrade by showing blurred-out post previews of
                posts that are only accessible to higher tiers.
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <RadioButton
                  value={formik.values.contentVisibility}
                  checkedValue="ShowLockedOnlyHigherLevel"
                  label="Yes, show post previews to lower tier members"
                  name="contentVisibility"
                  onChange={formik.handleChange}
                />
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <RadioButton
                  value={formik.values.contentVisibility}
                  checkedValue="ShowOnlyAccessible"
                  label="No, do not show post previews"
                  name="contentVisibility"
                  onChange={formik.handleChange}
                />
              </Fieldset.Item>
            </Fieldset.Row>
          </Fieldset>
        </Subsection>

        <Subsection title="Downloadable audio files">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                <Checkbox
                  {...formik.getFieldProps('audioDownload')}
                  label="Allow members to download audio posts"
                />
              </Fieldset.Item>
            </Fieldset.Row>
          </Fieldset>
        </Subsection>

        <Subsection title="Publicly show the number of members in each tier">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                Allow anyone to see how many active members are in each membership tier. The number
                will be visible on all pages where your membership tiers are shown.
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <Checkbox
                  {...formik.getFieldProps('membersCountEnabled')}
                  label="Show active members count for each membership tier"
                />
              </Fieldset.Item>
            </Fieldset.Row>
          </Fieldset>
        </Subsection>

        {!isProduction() && (
          <Subsection title="Adult/mature content">
            <Fieldset>
              <Fieldset.Row>
                <Fieldset.Item>
                  Ask visitors to confirm they are over 18 years old and alert them that some of my
                  content may be for adults only.
                </Fieldset.Item>
              </Fieldset.Row>
              <Fieldset.Row>
                <Fieldset.Item>
                  <Checkbox
                    {...formik.getFieldProps('adultContent')}
                    label="Ask visitors to confirm they are over 18 before they view my content"
                  />
                </Fieldset.Item>
              </Fieldset.Row>
            </Fieldset>
          </Subsection>
        )}

        <Subsection title="Show memberships on my site">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                When enabled, the members-only pages and a login link will appear in the header of
                your site.
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <Checkbox
                  {...formik.getFieldProps('membershipsVisible')}
                  disabled={
                    !accountSettingsQuery.data?.membershipsVisibility.requirements.unpublic
                      .noActiveSupporters
                  }
                  tooltip={
                    accountSettingsQuery.data?.membershipsVisibility.requirements.unpublic
                      .noActiveSupporters
                      ? undefined
                      : 'Memberships cannot be hidden if there are active members.'
                  }
                  label="Show memberships on my site"
                />
              </Fieldset.Item>
            </Fieldset.Row>
          </Fieldset>
        </Subsection>

        {dashboardFeatureFlags.membershipsGifting.enabled && (
          <Subsection title="Allow memberships to be gifted">
            <Fieldset>
              <Fieldset.Row>
                <Fieldset.Item>
                  Allow memberships to be purchased as a gift for someone else. Learn more
                </Fieldset.Item>
              </Fieldset.Row>
              <Fieldset.Row>
                <Fieldset.Item>
                  <Checkbox
                    {...formik.getFieldProps('gifEnabled')}
                    label="Allow memberships to be gifted"
                  />
                </Fieldset.Item>
              </Fieldset.Row>
            </Fieldset>
          </Subsection>
        )}
      </Loading>
      <SaveShortcutListener disabled={!formik.dirty} onSave={formik.submitForm} />
      <ConfirmBar
        isOpen={formik.dirty}
        isLoading={updateGeneralSettingsMutation.isLoading}
        onConfirm={formik.submitForm}
        onCancel={formik.resetForm}
      />
      <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
    </form>
  )
}
